import { types, flow, cast } from "mobx-state-tree"
import { createContext, useContext } from "react"
import SaleOrderStore from "./sale-order-store"
import InventoryStore from "./inventory-store"
import InventoryViewStore from "./inventory-view-store"
import InboundDeliveryStore from "./inbound-delivery-store"
import ReturnStore from "./return-store"
import BillOfMaterialStore from "./bom-store"
import { LoggingHelper } from "utils/logging-helper"
import { LoggingLevel } from "shared/constants/logging-level"
import { generalBusiness } from "businesses/general-business"
import * as AppHelper from "utils/app-helper"
import { sortObject } from "utils/sorting"
import * as StorageHelper from "utils/storage-helper"
import { storageName } from "shared/constants/storage-constant"
import DGItemProfileStore from "./dg-item-profile-store"
import { GlobalData } from "shared/global-data"
import FileUploadHistoryStore from "./file-upload-history-store"

const CompanyListModel = types.model("CompanyListModel").props({
  company: types.maybeNull(types.string),
  name: types.maybeNull(types.string),
})

const PermissionListModel = types.model("PermissionListModel").props({
  Permission_Name: types.maybeNull(types.string),
})

const RootModel = types
  .model({
    SaleOrderStore,
    InventoryStore,
    InventoryViewStore,
    InboundDeliveryStore,
    ReturnStore,
    BillOfMaterialStore,
    DGItemProfileStore,
    FileUploadHistoryStore,
    permissionList: types.optional(types.array(PermissionListModel), []),
    companyList: types.optional(types.array(CompanyListModel), []),
    isSuperUser: types.maybeNull(types.boolean),
    company: types.maybeNull(types.string),
    screenTitle: types.maybeNull(types.string),
    userName: types.maybeNull(types.string),
    userEmail: types.maybeNull(types.string),
    openForm: types.maybeNull(types.boolean),
    isAuthenticated: types.maybeNull(types.boolean),
    userConfigs: types.maybeNull(types.frozen()),
    selectedUserConfigId: types.maybeNull(types.integer, 0),
    notifications: types.maybeNull(types.frozen()),
    openNotification: types.maybeNull(types.boolean),
    companyWarehouses: types.optional(types.array(types.string), []),
    carrierService: types.maybeNull(types.frozen()),
  })
  .views(self => ({
    get shouldShowSaleOrder() {
      return AppHelper.hasPermission(self.permissionList, "", "Read")
    },
    get shouldShowInventory() {
      return AppHelper.hasPermission(self.permissionList, "", "Read")
    },
    get shouldShowInboundDelivery() {
      return AppHelper.hasPermission(self.permissionList, "", "Read")
    },
    get shouldShowReturns() {
      return AppHelper.hasPermission(self.permissionList, "", "Read")
    },
    get shouldSwitchCompany() {
      return AppHelper.hasPermission(self.permissionList, "", "CompanySelect")
    },
  }))
  .views(self => ({
    get isUserAuthorized() {
      return self.shouldShowSaleOrder || self.shouldShowInventory || self.shouldShowInboundDelivery || self.shouldShowReturns
    },
    get notificaton() {
      return self.notifications?.length > 0 ? self.notifications[0].Notification_Content.replaceAll("\\n", "<br/>") : ""
    },
  }))
  .actions(self => ({
    setIsSuperUser(data) {
      self.isSuperUser = data
    },
    setIsAuthenticated(data) {
      self.isAuthenticated = data
    },
    setUserName(data) {
      self.userName = data
    },
    setUserEmail(data) {
      self.userEmail = data
    },
    setCompany(data) {
      self.company = data
    },
    setPermissionList(data) {
      self.permissionList = cast(data)
    },
    setCompanyList(data) {
      self.companyList = cast(data)
    },
    setScreenTitle(data) {
      self.screenTitle = data
    },
    setOpenForm(data) {
      self.openForm = data
    },
    setOpenNotification(data) {
      self.openNotification = data
    },
    setUserConfigs(val) {
      self.userConfigs = val
    },
    setSelectedUserConfigId(val) {
      self.selectedUserConfigId = val
    },
    setCompanyWarehouses(data) {
      self.companyWarehouses = cast(data)
    },
    setCarrierService(data) {
      self.carrierService = data
    },
  }))
  .actions(self => ({
    loadPermissionList: flow(function* () {
      try {
        const data = yield generalBusiness.getUserPermissions(self.userEmail)
        self.setPermissionList(data)
      } catch (error) {
        LoggingHelper.writeLog("root-store", "loadPermissionList", `${error ? JSON.stringify(error) : ""}`, LoggingLevel.Error)
        self.setPermissionList([])
      }
    }),
    loadCompanyList: flow(function* () {
      try {
        const data = yield generalBusiness.getCompanyList()
        self.setCompanyList(data)
      } catch (error) {
        LoggingHelper.writeLog("root-store", "loadCompanyList", `${error ? JSON.stringify(error) : ""}`, LoggingLevel.Error)
        self.setCompanyList([])
      }
    }),
    loadProductList: flow(function* () {
      try {
        const productList = StorageHelper.getStorage(storageName.productList)
        let data = productList ? JSON.parse(productList) : []
        if (!Array.isArray(data) || !data.length) {
          data = yield generalBusiness.getProductList()
          // A failed request comes back as an empty list. Caching that would mark every SKU
          // "not available" on every order until the user logs out, so only cache a real list.
          if (Array.isArray(data) && data.length) StorageHelper.setStorage(storageName.productList, JSON.stringify(data))
          else data = []
        }

        GlobalData.ProductListSelection = data.map(option => ({ label: option.item, id: option.item, item_type: option.item_type, lot_controlled: option.lot_controlled }))
      } catch (error) {
        LoggingHelper.writeLog("root-store", "loadProductList", `${error ? JSON.stringify(error) : ""}`, LoggingLevel.Error)
        GlobalData.ProductListSelection = []
      }
    }),
    loadAllFilterCriteria: flow(function* () {
      try {
        let data
        const filterCriteria = StorageHelper.getStorage(storageName.filterCriteria)
        if (filterCriteria) {
          data = JSON.parse(filterCriteria)
        } else {
          data = yield generalBusiness.getFilterCriteria()
          StorageHelper.setStorage(storageName.filterCriteria, JSON.stringify(data))
        }

        GlobalData.FilterCriteria = data
      } catch (error) {
        LoggingHelper.writeLog("root-store", "loadAllFilterCriteria", `${error ? JSON.stringify(error) : ""}`, LoggingLevel.Error)
        GlobalData.FilterCriteria = []
      }
    }),
    loadCarrierService: flow(function* () {
      try {
        const data = yield generalBusiness.getCarrierService()
        self.setCarrierService(data)
      } catch (error) {
        LoggingHelper.writeLog("root-store", "loadCarrierService", `${error ? JSON.stringify(error) : ""}`, LoggingLevel.Error)
        self.setCarrierService([])
      }
    }),
    loadUserConfigs: flow(function* () {
      try {
        self.setUserConfigs({})
        const data = yield generalBusiness.getUserConfigs(self.userEmail, self.company)
        if (data) {
          const res = {}
          data.forEach(item => {
            if (item.Config_Value) {
              const json = JSON.parse(item.Config_Value)
              if (!res[json.Module]) res[json.Module] = []
              res[json.Module].push({
                Id: item.Id,
                Config_Name: item.Config_Name,
                Config_Value: json,
              })
            }
          })
          self.setUserConfigs(res)
        }
      } catch (error) {
        LoggingHelper.writeLog("root-store", "loadUserConfigs", `${error ? JSON.stringify(error) : ""}`, LoggingLevel.Error)
      }
    }),
    loadCompanyWarehouses: flow(function* () {
      try {
        const data = yield generalBusiness.getCompanyWarehouses(self.company)
        self.setCompanyWarehouses(data)
      } catch (error) {
        LoggingHelper.writeLog("root-store", "loadCompanyWarehouses", `${error ? JSON.stringify(error) : ""}`, LoggingLevel.Error)
      }
    }),
    addOrUpdateUserConfig: flow(function* (data, shouldCreateNewFilter) {
      const result = shouldCreateNewFilter
        ? yield generalBusiness.addUserConfig(data, self.userName, self.company, self.userEmail)
        : yield generalBusiness.updateUserConfig(self.selectedUserConfigId, data, self.userName)
      return result?.STATUS_CODE == 200
    }),
    deleteUserConfig: flow(function* (id) {
      const result = yield generalBusiness.deleteUserConfig(id)
      return result?.STATUS_CODE == 200
    }),
    getNotifications: flow(function* () {
      try {
        const data = yield generalBusiness.getNotifications()

        if (!self.openNotification && self.notifications?.length != data.length) self.setOpenNotification(true)

        self.notifications = data
      } catch (error) {
        LoggingHelper.writeLog("root-store", "getNotifications", `${error ? JSON.stringify(error) : ""}`, LoggingLevel.Error)
        self.notifications = []
      }
    }),
    sendEmail: flow(function* (payload) {
      try {
        const result = yield generalBusiness.sendEmail(payload)
        return result?.status == "created"
      } catch (error) {
        LoggingHelper.writeLog("root-store", "sendEmail", `${error ? JSON.stringify(error) : ""}`, LoggingLevel.Error)
        return false
      }
    }),
  }))
  .actions(self => ({
    loadInitialData: flow(function* () {
      try {
        yield self.loadPermissionList()

        if (self.isSuperUser) {
          yield self.loadCompanyList()
        }

        if (self.company) {
          yield Promise.all([self.loadProductList(), self.loadAllFilterCriteria(), self.loadCarrierService(), self.loadUserConfigs(), self.loadCompanyWarehouses()])
          self.getNotifications()
        }
      } catch (error) {
        LoggingHelper.writeLog("root-store", "loadInitialData", `${error ? JSON.stringify(error) : ""}`, LoggingLevel.Error)
      }
    }),
    getOptionsByFilter(filterName) {
      if (!GlobalData.FilterCriteria || !Array.isArray(GlobalData.FilterCriteria)) return []

      return GlobalData.FilterCriteria.filter(x => x.FILTER_NAME === filterName && (filterName === "WAREHOUSE" && self.companyWarehouses?.length ? self.companyWarehouses.includes(x.SYS_VALUE) : true))
        .sort((a, b) => sortObject(a, b, "EXTENDED_VALUE"))
        .map(x => {
          return { label: x.EXTENDED_VALUE, value: x.SYS_VALUE, identifier: x.IDENTIFIER }
        })
    },
    isProductAvailable(product) {
      if (!GlobalData.ProductListSelection || !Array.isArray(GlobalData.ProductListSelection)) return false

      return GlobalData.ProductListSelection.find(x => x.id === product)
    },
    isLotControlled(product) {
      if (!product || !GlobalData.ProductListSelection || !Array.isArray(GlobalData.ProductListSelection)) return false

      const found = GlobalData.ProductListSelection.find(x => x.id?.toUpperCase() === product?.toUpperCase())
      return found?.lot_controlled === "Y"
    },
    getTextByValue(filterName, code) {
      if (!GlobalData.FilterCriteria || !Array.isArray(GlobalData.FilterCriteria)) return code

      const found = GlobalData.FilterCriteria.find(x => x.FILTER_NAME === filterName && x.SYS_VALUE == code)
      return found ? found.EXTENDED_VALUE : code
    },
    reset() {
      self.setCompany("")
      self.setCompanyList([])
      self.setIsSuperUser(false)
      self.setPermissionList([])
      StorageHelper.setStorage(storageName.activeCompany, "")
      self.setIsAuthenticated(false)
      StorageHelper.deleteStorage(storageName.productList)
      StorageHelper.deleteStorage(storageName.filterCriteria)
      StorageHelper.deleteStorage(storageName.importingModule)
    },
  }))

export const rootStore = RootModel.create({
  SaleOrderStore: {
    data: [],
  },
  InventoryStore: {
    data: [],
  },
  InventoryViewStore: {
    data: [],
  },
  InboundDeliveryStore: {
    data: [],
  },
  BillOfMaterialStore: {
    data: [],
  },
  ReturnStore: {
    data: [],
  },
  DGItemProfileStore: {
    data: [],
  },
  FileUploadHistoryStore: {
    data: [],
  },
})

export const RootStoreContext = createContext()

export const RootStoreProvider = RootStoreContext.Provider

export const useStores = () => {
  const store = useContext(RootStoreContext)
  if (store === null) {
    throw new Error("Store cannot be null, please add a context provider")
  }
  return store
}
